import { useCallback, useEffect, useState } from "react";
import { submitInstallation, uploadInstallationPhoto } from "./api";
import type { InstallationPhotoType, InstallationStatus } from "./types";

/**
 * Installation-domain offline support (TASK-008-frontend).
 * Mirrors the flow-02 pattern at installation scope:
 *  - task-list cache ("as of HH:MM" offline variant)
 *  - payment-summary cache (ledger ≤ 24 h ruling, Lead ruling 1)
 *  - in-memory photo File registry + localStorage upload/submit queues,
 *    replayed on the next `online` event.
 *
 * LIMITATION (same as flow-02): File bytes live only for the live browser
 * session; queue metadata persists and un-uploadable items surface as failed.
 */

const TASK_CACHE_KEY = "pg_inst_tasks_v1";
const PAYMENT_CACHE_KEY = "pg_inst_payment_v1";
const PHOTO_META_KEY = "pg_inst_photo_meta_v1";
const QUEUE_KEY = "pg_inst_queue_v1";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort */
  }
}

// ── Task-list cache (S1 offline variant) ──────────────────────────────────────

export interface TaskCacheRow {
  id: number;
  customer_id: number;
  product_id: number | null;
  contract_id: number | null;
  status: InstallationStatus;
  scheduled_date: string | null;
  created_at: string;
  /** Enrichment captured at cache time (name/ward/product are not on the row). */
  customer_name?: string;
  ward?: string;
  product_name?: string;
  contract_number?: string;
}

export interface TaskCache {
  at: string;
  rows: TaskCacheRow[];
}

export function readTaskCache(): TaskCache | null {
  return read<TaskCache | null>(TASK_CACHE_KEY, null);
}

export function writeTaskCache(rows: TaskCacheRow[]): void {
  write(TASK_CACHE_KEY, { at: new Date().toISOString(), rows });
}

// ── Payment cache (S4 cached ledger, ≤ 24 h) ──────────────────────────────────

export interface PaymentCacheEntry {
  at: string;
  installationId: number;
  contractId: number | null;
  contractNumber: string | null;
  tierName: string | null;
  durationMonths: number | null;
  depositRequired: number | null;
  amountPaid: number;
  arrears: number;
}

export function readPaymentCache(installationId: number): PaymentCacheEntry | null {
  const all = read<Record<string, PaymentCacheEntry>>(PAYMENT_CACHE_KEY, {});
  return all[String(installationId)] ?? null;
}

export function writePaymentCache(entry: PaymentCacheEntry): void {
  const all = read<Record<string, PaymentCacheEntry>>(PAYMENT_CACHE_KEY, {});
  all[String(entry.installationId)] = entry;
  write(PAYMENT_CACHE_KEY, all);
}

export function paymentCacheAgeHours(entry: PaymentCacheEntry): number {
  return (Date.now() - new Date(entry.at).getTime()) / 3_600_000;
}

// ── Photo metadata + File registry (S3) ───────────────────────────────────────

export type InstPhotoState = "pending" | "uploading" | "failed" | "uploaded";

export interface InstPhotoMeta {
  state: InstPhotoState;
  url?: string;
  name?: string;
}

export function readPhotoMeta(installationId: number): Partial<Record<InstallationPhotoType, InstPhotoMeta>> {
  const all = read<Record<string, Partial<Record<InstallationPhotoType, InstPhotoMeta>>>>(PHOTO_META_KEY, {});
  return all[String(installationId)] ?? {};
}

export function setPhotoMeta(
  installationId: number,
  photoType: InstallationPhotoType,
  meta: InstPhotoMeta,
): void {
  const all = read<Record<string, Partial<Record<InstallationPhotoType, InstPhotoMeta>>>>(PHOTO_META_KEY, {});
  all[String(installationId)] = { ...(all[String(installationId)] ?? {}), [photoType]: meta };
  write(PHOTO_META_KEY, all);
}

const instPhotoFiles = new Map<string, File>();

export function registerInstPhotoFile(installationId: number, photoType: InstallationPhotoType, file: File): void {
  instPhotoFiles.set(`${installationId}:${photoType}`, file);
}

export function getInstPhotoFile(installationId: number, photoType: InstallationPhotoType): File | undefined {
  return instPhotoFiles.get(`${installationId}:${photoType}`);
}

// ── Mutation queue (photo uploads + submit) ───────────────────────────────────

export type InstQueuedMutation =
  | { queueId: string; kind: "photo-upload"; installationId: number; photoType: InstallationPhotoType; createdAt: string }
  | { queueId: string; kind: "submit"; installationId: number; idempotencyKey: string; createdAt: string };

export function listInstQueue(): InstQueuedMutation[] {
  return read<InstQueuedMutation[]>(QUEUE_KEY, []);
}

export function enqueueInst(item: InstQueuedMutation): void {
  const queue = listInstQueue();
  // one submit per installation
  if (item.kind === "submit" && queue.some((q) => q.kind === "submit" && q.installationId === item.installationId)) return;
  write(QUEUE_KEY, [...queue, item]);
}

export function dequeueInst(queueId: string): void {
  write(QUEUE_KEY, listInstQueue().filter((q) => q.queueId !== queueId));
}

export function instQueueCount(): number {
  return listInstQueue().length;
}

/** Replay the installation queue; called on reconnect. */
export async function syncInstQueue(): Promise<void> {
  for (const item of listInstQueue()) {
    try {
      if (item.kind === "photo-upload") {
        const file = getInstPhotoFile(item.installationId, item.photoType);
        if (!file) {
          setPhotoMeta(item.installationId, item.photoType, { state: "failed" });
          dequeueInst(item.queueId);
          continue;
        }
        setPhotoMeta(item.installationId, item.photoType, { state: "uploading" });
        const res = await uploadInstallationPhoto(item.installationId, item.photoType, file);
        setPhotoMeta(item.installationId, item.photoType, { state: "uploaded", url: res.photo.photo_url });
      } else {
        await submitInstallation(item.installationId, item.idempotencyKey);
      }
      dequeueInst(item.queueId);
    } catch {
      if (item.kind === "photo-upload") {
        setPhotoMeta(item.installationId, item.photoType, { state: "failed" });
      }
      /* stays queued for the next online event */
    }
  }
}

/** Hook: live online status + queue replay on reconnect + pending count. */
export function useInstallationSync() {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [pending, setPending] = useState<number>(() => instQueueCount());
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(() => setPending(instQueueCount()), []);

  const sync = useCallback(async () => {
    if (!navigator.onLine || instQueueCount() === 0) {
      refresh();
      return;
    }
    setSyncing(true);
    try {
      await syncInstQueue();
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [refresh]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (online) void sync();
  }, [online, sync]);

  return { online, pending, syncing, sync, refresh };
}

export function formatHHMM(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
